// Device-linking transfer format (Layer CH6c). What the Primary streams to the
// New device over the CH6b secure channel — plaintext message history, in
// bounded chunks. The relay only ever sees these frames sealed (chatDb/linking),
// and the crypto is proven in rust/bz-mls; this module is just the framing.
//
// Flow over the channel (after the SAS handshake is ready):
//   New → Primary:  hello { cursor }              (what I already have)
//   Primary → New:  manifest { convs }            (what you're about to get)
//   Primary → New:  chunk { convId, messages, done }   × N   (bounded by size)
//   Primary → New:  done
//   New → Primary:  complete { received }         (so the Primary can close)

import type { StoredMessage } from './chatDb';

/** Must match rust/bz-mls LINK_VERSION and the QR's version byte. */
export const LINK_VERSION = 1;

/** The display-relevant conversation fields; MLS state is device-local and is
 * NEVER transferred (the New device already has its own via roster reconcile). */
export interface LinkConvMeta {
  id: string;
  kind: 'dm' | 'group';
  peerUserId?: string;
  initiatedByMe?: boolean;
  /** Highest seq the Primary holds — lets the New device show a count/target. */
  maxSeq: number;
}

export interface LinkHello {
  v: typeof LINK_VERSION;
  t: 'hello';
  /** convId → highest seq the New device already has (empty on a first link). */
  cursor: Record<string, number>;
}

export interface LinkManifest {
  v: typeof LINK_VERSION;
  t: 'manifest';
  convs: LinkConvMeta[];
}

export interface LinkChunk {
  v: typeof LINK_VERSION;
  t: 'chunk';
  convId: string;
  messages: StoredMessage[];
  /** True on the last chunk of this conversation. */
  done: boolean;
}

export interface LinkDone {
  v: typeof LINK_VERSION;
  t: 'done';
}

export interface LinkComplete {
  v: typeof LINK_VERSION;
  t: 'complete';
  received: number;
}

export type LinkMessage = LinkHello | LinkManifest | LinkChunk | LinkDone | LinkComplete;

const enc = new TextEncoder();
const dec = new TextDecoder();

export function encodeLink(m: LinkMessage): Uint8Array {
  return enc.encode(JSON.stringify(m));
}

/** Decode + validate a transfer frame. Throws on anything not a known v1 shape —
 * a malformed frame on an authenticated channel is a bug or an attack, never
 * legacy data, so unlike the message wire this does not fall back to text. */
export function decodeLink(bytes: Uint8Array): LinkMessage {
  const raw = JSON.parse(dec.decode(bytes)) as Partial<LinkMessage>;
  if (!raw || raw.v !== LINK_VERSION || typeof raw.t !== 'string') {
    throw new Error('link: malformed transfer frame');
  }
  switch (raw.t) {
    case 'hello':
      if (typeof (raw as LinkHello).cursor !== 'object' || (raw as LinkHello).cursor === null) break;
      return raw as LinkHello;
    case 'manifest':
      if (!Array.isArray((raw as LinkManifest).convs)) break;
      return raw as LinkManifest;
    case 'chunk': {
      const c = raw as LinkChunk;
      if (typeof c.convId === 'string' && Array.isArray(c.messages) && typeof c.done === 'boolean') {
        return c;
      }
      break;
    }
    case 'done':
      return raw as LinkDone;
    case 'complete':
      if (typeof (raw as LinkComplete).received === 'number') return raw as LinkComplete;
      break;
  }
  throw new Error(`link: invalid ${raw.t} frame`);
}

/** What a scanned QR carries: version, the rendezvous id, and the Primary's
 * ephemeral public key (base64). The account id is NOT here — each side binds
 * its OWN authenticated user id, so a cross-account attempt fails the SAS. */
export interface LinkQr {
  v: number;
  rid: string;
  pub: string;
}

export function encodeQr(qr: LinkQr): string {
  return JSON.stringify(qr);
}

export function parseQr(text: string): LinkQr {
  const raw = JSON.parse(text) as Partial<LinkQr>;
  if (
    !raw ||
    typeof raw.v !== 'number' ||
    typeof raw.rid !== 'string' ||
    typeof raw.pub !== 'string'
  ) {
    throw new Error('link: unrecognized QR');
  }
  if (raw.v !== LINK_VERSION) {
    throw new Error(`link: QR is version ${raw.v}, this device speaks ${LINK_VERSION}`);
  }
  return raw as LinkQr;
}

/** Greedily split a conversation's messages into chunks that stay well under the
 * relay's per-frame ceiling (256 KiB) once JSON-encoded and sealed. Never
 * returns an empty array — a conversation with no new messages still yields one
 * empty, `done` chunk so the receiver sees the conversation close. */
export function chunkBySize(convId: string, messages: StoredMessage[], limit = 128 * 1024): LinkChunk[] {
  const chunks: LinkChunk[] = [];
  let batch: StoredMessage[] = [];
  let size = 0;
  for (const m of messages) {
    const mSize = JSON.stringify(m).length;
    if (batch.length > 0 && size + mSize > limit) {
      chunks.push({ v: LINK_VERSION, t: 'chunk', convId, messages: batch, done: false });
      batch = [];
      size = 0;
    }
    batch.push(m);
    size += mSize;
  }
  chunks.push({ v: LINK_VERSION, t: 'chunk', convId, messages: batch, done: true });
  return chunks;
}
